import { ReactNode } from 'react';
import { LoaderProvider } from 'shared';

import { BillingPackagesProvider } from './BillingPackages.provider';
import { CacheProvider } from './Cache.provider';
import { ConfirmationDialogueProvider } from './ConfirmationDialogue.provider';
import { FeatureFlagProvider } from './FeatureFlag.provider';
import { InvoiceDetailsProvider } from './InvoiceDetails.provider';
import { NavMenuProvider } from './NavMenu.provider';
import { PartnerAccountsProvider } from './PartnerAccounts.provider';
import { PartnerAccountBusinessCaseProvider } from './PartnerAccountsBusinessCase.provider';
import { PartnerLoginProvider } from './PartnerLogin.provider';
import { PartnerDataProvider } from './PartnerData.provider';
import { PermissionsProvider } from './Permissions.provider';
import { DialogProvider } from './Dialog.provider';
import { DatadogProvider } from './Datadog.provider';
import { HubSpotDropdownDataSetProvider } from './HubSpotDropdownData.provider';
import { HubSpotDataProvider } from './HubSpotData.provider';
import { CompanyProfileFieldsProvider } from './CompanyProfileFields.provider';
import { DocumentProvider } from './Document.provider';
import { PartnerAccountMatrixProvider } from './PartnerAccountsMatrix.provider';
import { AccountTransactionTypesProvider } from './AccountTransactionTypes.provider';
import { LockedFeatureDialogue } from './LockedFeatureDialogue.provider';
import { BrandingContextProvider } from './Branding.provider';
import { AlertDialogProvider } from './AlertDialog.provider';
import { SessionProvider } from './Session.provider';
import { LogTimestampProvider } from './LogTimestamp.provider';
import { LoggingProvider } from './Logging.provider';
import { I18nextProvider } from 'react-i18next';
import { MSEntraProvider } from './MsEntra.provider';
import { ToastContextProvider } from './Toast.provider';
import SharedServicesProvider from './SharedServicesProvider';
import i18n from '../i18n/i18n';

export const MainProvider = ({ children }: { children: ReactNode }): React.JSX.Element => (
  /* Shared's fullscreen blocking loader. Outermost: any provider below may call
  useBlockingLoader (via useDataFetchIndicator), which throws without it. */
  <LoaderProvider>
    <MSEntraProvider>
      <CacheProvider>
        <SessionProvider>
          <LogTimestampProvider>
            <DatadogProvider>
              <I18nextProvider i18n={i18n}>
                <PermissionsProvider>
                  <PartnerDataProvider>
                    <FeatureFlagProvider>
                      <LockedFeatureDialogue>
                        <ConfirmationDialogueProvider>
                          {/* Above DialogProvider and AlertDialogProvider: their dialog content renders as
                              a sibling of children, so it only sees contexts provided above them. */}
                          <ToastContextProvider>
                            <DialogProvider>
                              <AlertDialogProvider>
                                <NavMenuProvider>
                                  <PartnerLoginProvider>
                                    <BrandingContextProvider>
                                      <BillingPackagesProvider>
                                        <DocumentProvider>
                                          <CompanyProfileFieldsProvider>
                                            <HubSpotDropdownDataSetProvider>
                                              <AccountTransactionTypesProvider>
                                                <PartnerAccountsProvider>
                                                  <PartnerAccountBusinessCaseProvider>
                                                    <PartnerAccountMatrixProvider>
                                                      <HubSpotDataProvider>
                                                        <LoggingProvider>
                                                          <InvoiceDetailsProvider>
                                                            {/* Keep SharedServicesProvider innermost: it renders shared's
                                                                dialog/toast/alert content as siblings of children, which must see every
                                                                host context. A shared hook inside a host-opened Modal is unsupported. */}
                                                            <SharedServicesProvider>{children}</SharedServicesProvider>
                                                          </InvoiceDetailsProvider>
                                                        </LoggingProvider>
                                                      </HubSpotDataProvider>
                                                    </PartnerAccountMatrixProvider>
                                                  </PartnerAccountBusinessCaseProvider>
                                                </PartnerAccountsProvider>
                                              </AccountTransactionTypesProvider>
                                            </HubSpotDropdownDataSetProvider>
                                          </CompanyProfileFieldsProvider>
                                        </DocumentProvider>
                                      </BillingPackagesProvider>
                                    </BrandingContextProvider>
                                  </PartnerLoginProvider>
                                </NavMenuProvider>
                              </AlertDialogProvider>
                            </DialogProvider>
                          </ToastContextProvider>
                        </ConfirmationDialogueProvider>
                      </LockedFeatureDialogue>
                    </FeatureFlagProvider>
                  </PartnerDataProvider>
                </PermissionsProvider>
              </I18nextProvider>
            </DatadogProvider>
          </LogTimestampProvider>
        </SessionProvider>
      </CacheProvider>
    </MSEntraProvider>
  </LoaderProvider>
);
